import { SolanaSignTransaction } from "@solana/wallet-standard-features";
import { getWallets } from "@wallet-standard/app";

import { solanaChain } from "@/lib/wallet/config";

import type { SolanaSignTransactionFeature } from "@solana/wallet-standard-features";
import type { Wallet } from "@wallet-standard/base";

/**
 * Phantom's Wallet Standard `solana:signTransaction`, which signs serialized
 * bytes in any version the wallet lists, including v1.
 *
 * `PhantomWalletAdapter` only signs legacy and v0, so on its own the SDK
 * prepares v0: a sponsored transaction without the explicit compute and
 * loaded-data limits Phantom needs to simulate it (its "This dApp could be
 * malicious" warning), and capped at 1232 bytes, which splits a 24-pack
 * combine into seven prompts. Signing v1 bytes here fixes both. It is the
 * route Phantom's own v1 recipe takes.
 *
 * The holder pays for and is the only signer of what Phantom signs, which is
 * what Phantom asks of a transaction it should simulate. Phantom may add its
 * own instructions to one; the SDK and API accept that as long as every
 * instruction we prepared is intact (`walletRewriteRefusal`, after #556).
 *
 * Looked up on every use rather than held, because the account list changes
 * as the holder connects, switches or disconnects.
 */
export interface ByteSigner {
  supportedTransactionVersions: readonly (0 | 1)[];
  advertisedTransactionVersions?: readonly ("legacy" | 0 | 1)[];
  signTransactionBytes(transaction: Uint8Array): Promise<Uint8Array>;
}

type SignTransaction =
  SolanaSignTransactionFeature[typeof SolanaSignTransaction];

function isSignTransaction(value: unknown): value is SignTransaction {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof Reflect.get(value, "signTransaction") === "function" &&
    Array.isArray(Reflect.get(value, "supportedTransactionVersions"))
  );
}

export function phantomByteSigner(
  address: string,
  wallets: readonly Wallet[] = getWallets().get()
): ByteSigner | null {
  for (const wallet of wallets) {
    if (wallet.name !== "Phantom") continue;
    const feature: unknown = wallet.features[SolanaSignTransaction];
    const account = wallet.accounts.find(
      (candidate) => candidate.address === address
    );
    if (!isSignTransaction(feature) || !account) continue;
    const versions = feature.supportedTransactionVersions.filter(
      (version): version is 0 | 1 => version === 0 || version === 1
    );
    // Only worth taking over from the adapter for v1.
    if (!versions.includes(1)) return null;
    return {
      supportedTransactionVersions: versions,
      advertisedTransactionVersions:
        feature.supportedTransactionVersions.filter(
          (version): version is "legacy" | 0 | 1 =>
            version === "legacy" || version === 0 || version === 1
        ),
      async signTransactionBytes(transaction) {
        const [output] = await feature.signTransaction({
          account,
          chain: solanaChain,
          transaction,
        });
        if (!output) throw new Error("Phantom returned no signed transaction.");
        return output.signedTransaction;
      },
    };
  }
  return null;
}
